import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { luckySpinAPI } from '../../../services/api';

export const fetchProgress = createAsyncThunk(
  'progress/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await luckySpinAPI.getProgress();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Забрать одну накопленную награду за этап (самую старую, либо по claimId).
// Ответ: { claimedReward, progress } — progress уже без забранной награды.
export const claimProgressReward = createAsyncThunk(
  'progress/claimReward',
  async (claimId = null, { rejectWithValue }) => {
    try {
      return await luckySpinAPI.claimProgressReward(claimId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  currentStage: 1,
  spinsInCurrentStage: 0,
  spinsRequiredForStage: 10,
  progressPercentage: 0,
  totalSpins: 0,
  stageReward: {
    type: 'COINS',
    value: 0,
    displayName: ''
  },
  // Заработанные, но ещё не собранные награды (oldest first):
  // [{ id, stage, spinsRequired, type, value, displayName }]
  pendingRewards: [],
  isClaiming: false,
  lastUpdate: null,
  isLoading: false,
  error: null,
};

// Единый маппер ответа GET /progress, WS user.progress.updated и progress из spin/claim
function applyProgress(state, d) {
  if (!d) return;
  if (d.totalSpins !== undefined) state.totalSpins = d.totalSpins;
  if (d.currentStage !== undefined) state.currentStage = d.currentStage;
  if (d.stageProgress) {
    state.spinsInCurrentStage = d.stageProgress.current ?? state.spinsInCurrentStage;
    state.spinsRequiredForStage = d.stageProgress.max ?? state.spinsRequiredForStage;
    state.progressPercentage = d.stageProgress.percentage ?? state.progressPercentage;
  } else {
    if (d.spinsInCurrentStage !== undefined) state.spinsInCurrentStage = d.spinsInCurrentStage;
    if (d.spinsRequiredForStage !== undefined) state.spinsRequiredForStage = d.spinsRequiredForStage;
    if (d.progressPercentage !== undefined) state.progressPercentage = d.progressPercentage;
  }
  const reward = d.nextReward ?? d.stageReward;
  if (reward) state.stageReward = reward;
  if (Array.isArray(d.pendingRewards)) state.pendingRewards = d.pendingRewards;
}

const progressSlice = createSlice({
  name: 'progress',
  initialState,
  reducers: {
    updateFromWebSocket: (state, action) => {
      applyProgress(state, action.payload);
      state.lastUpdate = new Date().toISOString();
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProgress.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchProgress.fulfilled, (state, action) => {
        state.isLoading = false;
        applyProgress(state, action.payload);
      })
      .addCase(fetchProgress.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(claimProgressReward.pending, (state) => { state.isClaiming = true; state.error = null; })
      .addCase(claimProgressReward.fulfilled, (state, action) => {
        state.isClaiming = false;
        applyProgress(state, action.payload?.progress);
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(claimProgressReward.rejected, (state, action) => {
        state.isClaiming = false;
        state.error = action.payload;
      });
  },
});

// Награда, которую сейчас показывает иконка у шкалы: самая старая несобранная,
// а если собирать нечего — награда текущего этапа.
export const selectDisplayedStageReward = (progress) => {
  const pending = progress?.pendingRewards?.[0];
  if (pending) {
    return {
      claimable: true,
      claimId: pending.id,
      stage: pending.stage,
      reward: { type: pending.type, value: pending.value, displayName: pending.displayName },
      current: pending.spinsRequired,
      max: pending.spinsRequired,
      percentage: 100,
    };
  }
  return {
    claimable: false,
    claimId: null,
    stage: progress?.currentStage,
    reward: progress?.stageReward,
    current: progress?.spinsInCurrentStage || 0,
    max: progress?.spinsRequiredForStage || 10,
    percentage: progress?.progressPercentage ?? 0,
  };
};

export const { updateFromWebSocket, clearError, setLoading } = progressSlice.actions;
export default progressSlice.reducer;
